import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CarteTerritoire,
  FOND_OPENFREEMAP,
  type Territoire,
  type GeometriePolygonale,
} from '@eec/ui';
import { messageErreur } from '../../api/erreurs';

interface Limites {
  features: {
    properties: { code?: string; nom: string; source: string; indicatif?: boolean };
    geometry: GeometriePolygonale;
  }[];
}
export interface EquipementCarte {
  id: string;
  code: string;
  nom: string;
  longitude: number;
  latitude: number;
  domaine: string;
  statut: string;
}
export async function lireLimites(signal: AbortSignal): Promise<Territoire[]> {
  const collections = await Promise.all(
    ['pays', 'provinces'].map(async (niveau) => {
      const r = await fetch(`/donnees-geo/gabon/${niveau}.geojson`, { signal });
      if (!r.ok) throw new Error('Les limites géographiques ne sont pas disponibles');
      return r.json() as Promise<Limites>;
    }),
  );
  const provinces = collections[1]!.features.map((f) => ({
    id: f.properties.code!,
    niveau: 'province' as const,
    libelle: f.properties.nom,
    geometrie: f.geometry,
    indicatif: Boolean(f.properties.indicatif),
    source: f.properties.source,
  }));
  return [
    {
      id: 'gabon',
      niveau: 'pays',
      libelle: 'Gabon',
      geometrie: collections[0]!.features[0]!.geometry,
      indicatif: false,
      source: 'OpenStreetMap © contributeurs, ODbL 1.0',
    },
    ...provinces.map((p) => ({
      ...p,
      voisins: provinces
        .filter((v) => v.id !== p.id)
        .map((v) => ({
          id: v.id,
          libelle: v.libelle,
          geometrie: v.geometrie,
          indicatif: v.indicatif,
        })),
    })),
  ];
}
/** Réemploi de la carte territoriale de CollectivitésConnect, avec les données métier autorisées. */
export function CarteEquipements({
  equipements,
  onSelection,
}: {
  equipements: EquipementCarte[];
  onSelection: (id: string) => void;
}) {
  const [echelle, setEchelle] = useState('gabon');
  const [domaine, setDomaine] = useState('');
  const [fond, setFond] = useState(false);
  const [selection, setSelection] = useState<string | null>(null);
  const limites = useQuery({
    queryKey: ['limites-cartographiques-gabon'],
    queryFn: ({ signal }) => lireLimites(signal),
    staleTime: Infinity,
  });
  const visibles = equipements.filter(
    (e) =>
      (!domaine || e.domaine === domaine) &&
      Number.isFinite(e.latitude) &&
      Number.isFinite(e.longitude),
  );
  const selectionner = (id: string) => {
    setSelection(id);
    onSelection(id);
  };
  return (
    <section aria-label="Carte des équipements" className="eec-carte">
      <h2>Les équipements sur le territoire</h2>
      <div className="eec-filtres">
        <label>
          Service affiché sur la carte
          <select
            value={domaine}
            onChange={(e) => {
              setDomaine(e.target.value);
              setSelection(null);
            }}
          >
            <option value="">Eau et électricité</option>
            <option value="WATER">Eau</option>
            <option value="ELECTRICITY">Électricité</option>
          </select>
        </label>
        <label>
          <input type="checkbox" checked={fond} onChange={(e) => setFond(e.target.checked)} />{' '}
          Afficher le fond de rues OpenFreeMap (connexion externe)
        </label>
      </div>
      <p>
        Le choix de province recadre la carte. Il ne modifie pas vos droits ni la liste des
        équipements.
      </p>
      {limites.isPending && <p role="status">Chargement des limites du Gabon…</p>}
      {limites.error && <p role="alert">{messageErreur(limites.error)}</p>}
      {limites.data && (
        <CarteTerritoire
          titre="Équipements géolocalisés"
          echelles={limites.data}
          echelle={echelle}
          onEchelle={setEchelle}
          fond={fond ? FOND_OPENFREEMAP : null}
          marqueurActif={selection}
          onMarqueur={selectionner}
          libelleObjets={{ singulier: 'équipement localisé', pluriel: 'équipements localisés' }}
          libellesTon={{
            ouvert: 'En service',
            retard: 'Dégradé ou hors service',
            autre: 'Maintenance ou retrait',
          }}
          marqueurs={visibles.map((e, i) => ({
            id: e.id,
            numero: i + 1,
            longitude: e.longitude,
            latitude: e.latitude,
            libelle: `${e.code} — ${e.nom}`,
            ton:
              e.statut === 'IN_SERVICE'
                ? 'ouvert'
                : ['DEGRADED', 'OUT_OF_SERVICE'].includes(e.statut)
                  ? 'retard'
                  : 'autre',
          }))}
        />
      )}
      <p>
        {visibles.length} équipement(s) localisé(s) dans la sélection. Limites OpenStreetMap, sans
        valeur de bornage officiel.
      </p>
      <details>
        <summary>Accéder aux fiches depuis la liste de la carte</summary>
        <ul>
          {visibles.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => selectionner(e.id)}>
                {e.code} — {e.nom}
              </button>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
